import { useState } from 'react';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import {
  formatInvoiceAmount,
  useGetStudentInvoicesBalanceQuery,
  type IStudentInvoicesBalance,
} from '@/entities/Invoices';
import { AppButton } from '@/shared/ui/AppButton';
import { useModalAutoFocus } from '@/shared/lib/hooks';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { dismissForToday, isDismissedToday, type ReminderKind } from '../lib/dismissedToday';

/**
 * Напоминание о счёте. Всплывает при заходе в кабинет, не чаще раза в сутки.
 *
 * Без ссылок намеренно: раздела финансов у студента нет, вести некуда. Задача плашки —
 * чтобы человек узнал о счёте до того, как ему закроют курс, а не в момент закрытия.
 *
 * Просрочка вытесняет «скоро срок»: новость строго хуже, а две модалки подряд закрывают
 * не читая.
 */

const dayWord = (days: number) => (days === 1 ? 'day' : 'days');

/**
 * `daysUntilSuspension` — живой отсчёт, но `null` в нём означает и «доступ уже закрыт»,
 * и «зеркало устарело». В этих случаях срок не обещаем, а говорим правило колледжа:
 * оно верно и на первый день просрочки, и на десятый.
 */
const overdueText = (daysUntilSuspension: number | null, graceDays: number) => {
  if (daysUntilSuspension === 0) {
    return 'Your payment is overdue. Access to the course is suspended today unless the invoice is settled.';
  }

  if (daysUntilSuspension !== null && daysUntilSuspension > 0) {
    return `Your payment is overdue. Access to the course is suspended in ${daysUntilSuspension} ${dayWord(daysUntilSuspension)} unless the invoice is settled.`;
  }

  return graceDays > 0
    ? `Your payment is overdue. An invoice left unpaid for more than ${graceDays} ${dayWord(graceDays)} suspends access to the course, so please settle it as soon as you can.`
    : 'Your payment is overdue. Please settle it as soon as you can — an unpaid invoice suspends access to the course.';
};

const dueSoonText = (daysUntilDue: number) => {
  if (daysUntilDue <= 0) return 'Your payment is due today.';
  if (daysUntilDue === 1) return 'Your payment is due tomorrow.';
  return `Your payment is due in ${daysUntilDue} days.`;
};

const resolveReminder = (balance: IStudentInvoicesBalance) => {
  if (balance.overdueCount > 0) {
    return {
      kind: 'overdue' as ReminderKind,
      title: 'Payment overdue',
      amount: formatInvoiceAmount(balance.overdueAmount, balance.currency),
      text: overdueText(balance.daysUntilSuspension, balance.graceDays),
    };
  }

  if (balance.dueSoonCount > 0 && balance.daysUntilDue !== null) {
    return {
      kind: 'due-soon' as ReminderKind,
      title: 'Payment due soon',
      amount: formatInvoiceAmount(balance.dueSoonAmount, balance.currency),
      text: dueSoonText(balance.daysUntilDue),
    };
  }

  return null;
};

export const PaymentReminder = () => {
  const { data: balance } = useGetStudentInvoicesBalanceQuery();
  const [closedKind, setClosedKind] = useState<ReminderKind | null>(null);

  const reminder = balance ? resolveReminder(balance) : null;
  const isVisible =
    Boolean(reminder) &&
    closedKind !== reminder?.kind &&
    !isDismissedToday(reminder?.kind ?? 'overdue');

  useModalAutoFocus(isVisible);

  if (!reminder) return null;
  if (!isVisible) return null;

  const handleClose = () => {
    dismissForToday(reminder.kind);
    setClosedKind(reminder.kind);
  };

  // Прочерк вместо суммы приходит при счетах в разных валютах — тогда строку не показываем.
  const hasAmount = reminder.amount !== '—';

  return (
    <Modal
      isOpen
      onOpenChange={handleClose}
      placement="center"
      motionProps={scaleInOut}
      size="md"
      classNames={{ base: 'p-6 flex flex-col gap-5' }}
    >
      <ModalContent>
        <>
          <ModalHeader className="p-0 text-2xl font-medium text-black-headline">
            {reminder.title}
          </ModalHeader>
          <ModalBody className="flex flex-col gap-3 p-0">
            {hasAmount && (
              <p className="text-3xl font-medium text-red-tertiary">{reminder.amount}</p>
            )}
            <p className="text-base text-black-secondary">{reminder.text}</p>
            <p className="text-sm text-grey-secondary">
              The college accepts payment by bank transfer. Contact the finance team if you need
              the details or think this is a mistake.
            </p>
          </ModalBody>
          <ModalFooter className="flex items-center justify-center p-0">
            <AppButton color="red" onPress={handleClose}>
              Close
            </AppButton>
          </ModalFooter>
        </>
      </ModalContent>
    </Modal>
  );
};
